import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { NS } from "./U3101";
import {
  doCreateSalaryHistory,
  doDeleteSalaryHistory,
  doGetSalaryHistory,
  doUpdateSalaryHistory,
  luHandleAppiError,
  parseU3101SalaryHistoryFromApi,
  type U3101SalaryHistory,
  type U3101SalaryHistoryCreatePayload,
  type U3101SalaryHistoryUpdatePayload,
} from "./U3101Service";

export interface U3101SalaryHistoryHandlersOptions {
  onError?: (error: unknown) => void;
}

/**
 * Historique des salaires (BL-0442) : liste, create now/later, update/cancel planned.
 * Ne pas appeler les `do*` depuis les composants.
 */
export function useU3101SalaryHistoryHandlers(options: U3101SalaryHistoryHandlersOptions = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = options;

  const handleError = useCallback(
    (error: unknown) => {
      if (onError) {
        onError(error);
        return;
      }
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
    },
    [onError, t, toast],
  );

  const fetch = useCallback(
    async (employeeId: string): Promise<U3101SalaryHistory | null> => {
      const id = employeeId?.trim();
      if (!id) return null;
      try {
        const response = await doGetSalaryHistory(axios, id);
        return parseU3101SalaryHistoryFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  const create = useCallback(
    async (
      employeeId: string,
      payload: U3101SalaryHistoryCreatePayload,
    ): Promise<U3101SalaryHistory | null> => {
      const id = employeeId?.trim();
      if (!id) return null;
      try {
        const response = await doCreateSalaryHistory(axios, id, payload);
        return parseU3101SalaryHistoryFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  const update = useCallback(
    async (
      employeeId: string,
      entryId: string,
      payload: U3101SalaryHistoryUpdatePayload,
    ): Promise<U3101SalaryHistory | null> => {
      const id = employeeId?.trim();
      const eid = entryId?.trim();
      if (!id || !eid) return null;
      try {
        const response = await doUpdateSalaryHistory(axios, id, eid, payload);
        return parseU3101SalaryHistoryFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  const remove = useCallback(
    async (employeeId: string, entryId: string): Promise<U3101SalaryHistory | null> => {
      const id = employeeId?.trim();
      const eid = entryId?.trim();
      if (!id || !eid) return null;
      try {
        const response = await doDeleteSalaryHistory(axios, id, eid);
        return parseU3101SalaryHistoryFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  return { fetch, create, update, remove };
}
